import React, { useState } from "react";
import { Link } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      panelTitle="Forgot Password"
      panelText="No problem. We will email you a secure link to set a new password."
      eyebrow="Account Recovery"
      title="Reset Your Password"
      subtitle="Enter your email and we will send you a reset link"
      footer={
        <div className="mt-6 text-center">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="back-to-login-link">
            <ArrowLeft className="h-4 w-4" /> Back to Sign In
          </Link>
        </div>
      }
    >
      {sent ? (
        <div className="mt-8 flex flex-col items-center rounded-lg bg-[#E4EEE8] p-6 text-center" data-testid="forgot-success">
          <CheckCircle2 className="h-8 w-8 text-[#527D63]" />
          <p className="mt-3 text-sm font-medium text-[#3f6650]">
            If that email exists, a reset link has been sent. Check your inbox.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
          <div className="space-y-2">
            <label htmlFor="email" className="flex items-center gap-1 text-xs font-semibold tracking-wide text-slate-600">
              <span className="text-primary">*</span> Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="you@example.com" className="h-11 pl-10" data-testid="forgot-email" />
            </div>
          </div>
          {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
          <Button type="submit" className="h-11 w-full text-base" disabled={loading} data-testid="forgot-submit">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send Reset Link
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
